import { MappingTemplate } from '@/features/canvas/api/import';
import { LinkageColumn, LinkedColumn } from '@/features/canvas/types/linkage';

/**
 * Apply template mappings to current columns by matching column names
 * @param template The template to apply
 * @param currentColumns The current CSV columns
 * @returns Array of LinkedColumn objects for matched columns
 */
export const applyTemplate = (
  template: MappingTemplate,
  currentColumns: LinkageColumn[]
): LinkedColumn[] => {
  return template.mappings
    .map(mapping => {
      // Find column by name (case-insensitive)
      // First try to match by stored column name, then by target title
      const columnName =
        (mapping as LinkedColumn & { columnName?: string }).columnName || mapping.linkedTargetTitle;
      const column = currentColumns.find(
        c =>
          c.name.toLowerCase() === columnName?.toLowerCase() ||
          // Also try to match by partial matching
          c.name.toLowerCase().includes(columnName?.toLowerCase() || '') ||
          columnName?.toLowerCase().includes(c.name.toLowerCase()) ||
          // Fallback: exact match
          c.name === columnName
      );

      if (column) {
        return {
          ...mapping,
          importedColumnUuid: column.id,
        };
      }
      return null;
    })
    .filter((mapping): mapping is LinkedColumn => mapping !== null);
};

/**
 * Store column names in template mappings for better matching
 * @param linkedColumns Current linked columns
 * @param currentColumns Current CSV columns
 * @returns LinkedColumn array with updated column names
 */
export const prepareMappingsForTemplate = (
  linkedColumns: LinkedColumn[],
  currentColumns: LinkageColumn[]
): LinkedColumn[] => {
  return linkedColumns.map(mapping => {
    const column = currentColumns.find(c => c.id === mapping.importedColumnUuid);
    return {
      ...mapping,
      // Store the original column name for better template matching
      columnName: column?.name || mapping.linkedTargetTitle,
    } as LinkedColumn & { columnName: string };
  });
};
